// Updated AskBox.jsx
import { ArrowUp } from "lucide-react";
import React, {
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";
import {
  History,
  PanelLeftClose,
  ChevronDown,
  ChevronRight,
  Bot,
  Zap,
  Route,
  Rss,
} from "lucide-react";
import { normalizeToolEvent } from "./utils/tools";
import { useWorkspace } from "../../context/WorkspaceContext";

const styles = {
  container: {
    width: "100%",
    maxWidth: "1050px",
    justifyContent: "center",
    marginBottom: "8px",
  },
  inputContainer: {
    position: "relative",
    backgroundColor: "#1a1a1a",
    borderRadius: "24px",
    border: "1px solid #333333",
    padding: "4px",
    transition: "border-color 0.15s ease",
  },
  inputContainerFocused: {
    borderRadius: "12px",
    padding: "2px",
    background: "linear-gradient(180deg, #BF6FE5 0%, #CA698F 100%)",
    backgroundOrigin: "border-box",
    backgroundClip: "padding-box, border-box",
    boxShadow: "none",
  },
  inputWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#1a1a1a",
    borderRadius: "12px",
    padding: "8px",
  },
  textarea: {
    flex: 1,
    backgroundColor: "transparent",
    border: "none",
    outline: "none",
    color: "#ffffff",
    fontSize: "16px",
    resize: "none",
    minHeight: "24px",
    maxHeight: "120px",
    fontFamily: "inherit",
    lineHeight: "1.5",
  },
  sendBtn: {
    background: "linear-gradient(180deg, #BF6FE5 0%, #CA698F 100%)",
    border: "none",
    borderRadius: "20px",
    width: "40px",
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "#ffffff",
    transition: "all 0.15s ease",
    minWidth: "40px",
  },
  sendBtnDisabled: {
    background:
      "linear-gradient(180deg, rgba(191, 111, 229, 0.6) 0%, rgba(202, 105, 143, 0.6) 100%)",
    cursor: "not-allowed",
    color: "#6b7280",
  },
  sendSpinner: {
    width: "16px",
    height: "16px",
    border: "2px solid transparent",
    borderTop: "2px solid currentColor",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  logsContainer: {
    marginTop: "1rem",
    backgroundColor: "#111111",
    border: "1px solid #333333",
    borderRadius: "12px",
    overflow: "hidden",
  },
  logsHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "0.75rem 1rem",
    backgroundColor: "#1a1a1a",
    borderBottom: "1px solid #333333",
    fontSize: "0.875rem",
    fontWeight: "500",
    color: "#ffffff",
  },
  logsContent: {
    maxHeight: "200px",
    overflowY: "auto",
    padding: "0.75rem 1rem",
  },
  // logLine: {
  //   fontSize: "0.875rem",
  //   color: "#9ca3af",
  //   marginBottom: "0.5rem",
  //   lineHeight: "1.4",
  // },
  logsToggle: {
    marginTop: "0.5rem",
    backgroundColor: "transparent",
    border: "1px solid #333333",
    borderRadius: "8px",
    color: "#9ca3af",
    padding: "0.5rem 0.75rem",
    fontSize: "0.875rem",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    width: "100%",
    justifyContent: "center",
    transition: "all 0.15s ease",
  },
  logsClose: {
    backgroundColor: "transparent",
    border: "none",
    color: "#9ca3af",
    cursor: "pointer",
    fontSize: "18px",
    padding: "0",
    width: "24px",
    height: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  errorAlert: {
    marginTop: "1rem",
    backgroundColor: "#7f1d1d",
    border: "1px solid #dc2626",
    borderRadius: "8px",
    padding: "0.75rem 1rem",
    color: "#fecaca",
    fontSize: "0.875rem",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  },
  // AskBox.jsx — add/update these in the `styles` object
  logHeading: {
    color: "#FFFFFF",
    fontWeight: 700,
    fontSize: "14px",
    margin: "6px 0",
  },
  logList: {
    margin: 0,
    paddingLeft: "18px",
  },
  logListItem: {
    fontSize: "12px",
    color: "#FFFFFFB2",
    lineHeight: 1.45,
    marginBottom: "4px",
  },
  logLine: {
    fontSize: "12px",
    color: "#FFFFFFB2",
    marginBottom: "0.5rem",
    lineHeight: "1.45",
  },
};

// Helper: generate a unique ID
function generateUniqueId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `id_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

// Helper: get a stable session id per dataset (optional; safe to remove if unused)
function getOrCreateSessionId(datasetId) {
  if (!datasetId) return "default";
  const key = `agent:sid:${datasetId}`;
  try {
    let sid = window.localStorage.getItem(key);
    if (!sid) {
      sid = generateUniqueId();
      window.localStorage.setItem(key, sid);
    }
    return sid;
  } catch {
    // print("default");
    return "default";
  }
}

export const AskBox = forwardRef(
  (
    {
      apiBase,
      datasetId,
      onAsked,
      onAnswered,
      onProgress,
      onProgressClear,
      onStatus,
      email,
      onToolEvent,
    },
    ref
  ) => {
    const [q, setQ] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [logs, setLogs] = useState([]);
    const [showLogs, setShowLogs] = useState(true);
    const [loadingStage, setLoadingStage] = useState("");
    const [isFocused, setIsFocused] = useState(false);

    const { workspace } = useWorkspace();
    const clientId = workspace?.clientId || "default";
    const sessionId = workspace?.sessionId || "default";

    const logEndRef = useRef(null);
    const textareaRef = useRef(null);

    useImperativeHandle(ref, () => ({
      prefill: (text) => {
        setQ(text);
        setTimeout(() => {
          if (textareaRef.current) {
            textareaRef.current.focus();
            textareaRef.current.setSelectionRange(text.length, text.length);
          }
        }, 0);
      },
    }));

    // polling + flow refs
    const pollTimerRef = useRef(null);
    const didNarrateRef = useRef(false);
    const lastEmailRef = useRef(null);
    const firstPollResetDoneRef = useRef(false);

    // logging guards
    const planShownRef = useRef(false);
    const summaryLoggedRef = useRef(false);
    const finalLoggedRef = useRef(false);
    const artifactsLoggedRef = useRef(false);

    // dedupe across polls
    const seenRef = useRef(new Set());

    const PLAN_UPDATE_PREFIX = "__plan_update__:";

    const appendLog = (line) => {
      if (!line) return;
      setLogs((prev) => [...prev, line]);
      onProgress?.(line);
    };

    const scrollToEnd = () =>
      logEndRef.current?.scrollIntoView({ behavior: "smooth" });
    useEffect(() => {
      scrollToEnd();
    }, [logs]);

    // Auto-resize textarea
    useEffect(() => {
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
        textareaRef.current.style.height =
          textareaRef.current.scrollHeight + "px";
      }
    }, [q]);

    const stopPolling = () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };

    useEffect(
      () => () => {
        stopPolling();
      },
      []
    );

    const safeJsonParse = async (response) => {
      try {
        const text = await response.text();
        return JSON.parse(text);
      } catch {
        return {};
      }
    };

    // ---------- helpers ----------
    const bulletize = (text) => {
      const lines = String(text || "")
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean);

      if (lines.length === 1) {
        const numbered = lines[0]
          .split(/\s(?=\d+\.\s)/g)
          .map((s) => s.replace(/^\d+\.\s/, "").trim())
          .filter(Boolean);
        if (numbered.length > 1) return numbered;
      }

      const items = [];
      for (const l of lines) {
        const chunks = l
          .split(/\s(?=\d+\.\s)/g)
          .map((s) => s.trim())
          .filter(Boolean);
        if (chunks.length > 1) {
          items.push(...chunks.map((s) => s.replace(/^\d+\.\s/, "").trim()));
        } else {
          items.push(l.replace(/^(\d+\.\s|-+\s|•\s)/, "").trim());
        }
      }
      return items;
    };

    const normalizeObj = (it) => {
      if (!it) return null;
      if (typeof it === "string") {
        try {
          return JSON.parse(it);
        } catch {
          return it;
        }
      }
      return it;
    };

    const extractDataString = (raw) => {
      const obj = normalizeObj(raw);
      if (!obj) return null;
      if (Array.isArray(obj)) {
        const first =
          obj.find(
            (x) => x && typeof x === "object" && typeof x.data === "string"
          ) ?? obj[0];
        if (first && typeof first?.data === "string") return first.data;
      } else if (typeof obj === "object") {
        if (typeof obj.data === "string") return obj.data;
      }
      if (typeof raw === "string") {
        const m = raw.match(/["']?data["']?\s*:\s*("([\s\S]*?)")/i);
        if (m) {
          try {
            return JSON.parse(m[1]);
          } catch {
            return m[2]?.replace(/\\"/g, '"');
          }
        }
      }
      return null;
    };

    const hasSummary = (raw) => {
      const obj = normalizeObj(raw);
      if (!obj) return false;
      if (Array.isArray(obj))
        return obj.some(
          (x) => x && typeof x.summary === "string" && x.summary.trim()
        );
      if (typeof obj === "object")
        return typeof obj.summary === "string" && obj.summary.trim();
      if (typeof raw === "string")
        return /["']?summary["']?\s*:\s*"/i.test(raw);
      return false;
    };

    const extractToolVal = (raw) => {
      const obj = normalizeObj(raw);
      if (!obj || typeof obj !== "object") return null;
      return obj.tool ?? obj?.data?.tool ?? null;
    };

    const askSpendChat = async () => {
      if (!q.trim() || busy) return;

      stopPolling();
      onAsked?.(q.trim());
      setBusy(true);
      setError(null);
      setLogs([]);
      setShowLogs(true);
      setLoadingStage("Initializing analysis...");
      onStatus?.("Orchestrator: Initializing analysis…");

      // reset flags
      didNarrateRef.current = false;
      firstPollResetDoneRef.current = false;
      planShownRef.current = false;
      summaryLoggedRef.current = false;
      finalLoggedRef.current = false;
      artifactsLoggedRef.current = false;
      seenRef.current = new Set();
      lastEmailRef.current = null;

      // mandated first two log lines
      // appendLog("⚙️ Orchestrator Agent running...");
      // appendLog("Running...");
      appendLog("Navigating your data...");
      // appendLog("Preparing plan and tools");

      try {
        // 1) Kick off the run
        const res = await fetch(`${apiBase}/orc/spendchat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            message: q.trim(),
            client_id: clientId,
            session_id: sessionId,
            ...(email ? { email } : {}),
          }),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json?.error || `HTTP ${res.status}`);

        lastEmailRef.current = json?.email || null;

        // Synchronous short-circuit
        if (json?.type === "text" && json?.text) {
          onAnswered?.({ answer: json.text });
          setBusy(false);
          setQ("");
          setLoadingStage("");
          onProgressClear?.();
          onStatus?.("Done");
          return;
        }

        setLoadingStage("Processing your request...");
        onStatus?.("Orchestrator: Polling started");

        const doPoll = async (useReset) => {
          if (didNarrateRef.current) return;

          const mkUrl = (path) => {
            const params = new URLSearchParams({ 
              limit: 1000,
              client_id: clientId,
              session_id: sessionId,
            });
            if (email) params.append("email", email);
            if (useReset) params.append("reset", 1);
            return `${apiBase}${path}?${params.toString()}`;
          };

          const [plannerRes = {}, toolsRes = {}, execFinalRes = {}] =
            await Promise.all([
              fetch(mkUrl("/orc/events-planner-results")).then(safeJsonParse),
              fetch(mkUrl("/orc/events-tool-results")).then(safeJsonParse),
              fetch(mkUrl("/orc/events-execute-agent-final-result")).then(
                safeJsonParse
              ),
            ]);

          // ---- Planner: show heading + data string(s) ----
          if (Array.isArray(plannerRes?.items) && plannerRes.items.length > 0) {
            const newPlanItems = [];

            plannerRes.items.forEach((it) => {
              const key = `planner:${
                typeof it === "string" ? it : JSON.stringify(it)
              }`;
              if (seenRef.current.has(key)) return;
              seenRef.current.add(key);

              const dataStr = extractDataString(it);
              if (typeof dataStr === "string" && dataStr.trim()) {
                const normalized = dataStr.replace(/\r\n/g, "\n");
                newPlanItems.push(normalized);
              }
            });

            if (newPlanItems.length > 0) {
              const isFirstPlanRender = !planShownRef.current;
              if (isFirstPlanRender) {
                appendLog("Plan");
                planShownRef.current = true;
              }
              newPlanItems.forEach((item) => {
                const segments = item
                  .split(/\n\s*\n+/)
                  .map((seg) => seg.trim())
                  .filter(Boolean);
                if (segments.length === 0) return;
                segments.forEach((seg) => {
                  appendLog(
                    isFirstPlanRender ? seg : `${PLAN_UPDATE_PREFIX}${seg}`
                  );
                });
              });
            }

            plannerRes.items.forEach((it) => {
              if (!summaryLoggedRef.current && hasSummary(it)) {
                appendLog("Summarising results for you");
                summaryLoggedRef.current = true;
              }
            });
          }

          // ---- Tools: Tool {i}: <tool value> ----
          if (Array.isArray(toolsRes?.items) && toolsRes.items.length > 0) {
            toolsRes.items.forEach((it, i) => {
              const key = `tool:${i}:${
                typeof it === "string" ? it : JSON.stringify(it)
              }`;
              if (seenRef.current.has(key)) return;
              seenRef.current.add(key);

              try {
                const toolVal = extractToolVal(it);
                if (
                  typeof toolVal === "string" &&
                  /^RUNNING_NOW_/i.test(toolVal)
                ) {
                  onStatus?.(toolVal);
                }
                if (toolVal) appendLog(`Tool ${i + 1}: ${toolVal}`);

                const obj = normalizeObj(it);
                if (
                  !summaryLoggedRef.current &&
                  obj &&
                  typeof obj === "object" &&
                  typeof obj.summary === "string" &&
                  obj.summary.trim()
                ) {
                  appendLog("Summarising results for you");
                  summaryLoggedRef.current = true;
                }

                const normalizedEvent = normalizeToolEvent(it);
                if (normalizedEvent) {
                  onToolEvent?.(normalizedEvent);
                }
              } catch {}
            });
          }

          // ---- Final state gate ----
          const runEnded = !!execFinalRes?.run_ended;
          if (runEnded && !finalLoggedRef.current) {
            appendLog("Summarising results for you and generating artifacts..");
            // appendLog("Generating artifacts..");
            finalLoggedRef.current = true;
            onStatus?.("Executor Agent: final result ready");
          }

          if (!didNarrateRef.current && runEnded) {
            didNarrateRef.current = true;
            stopPolling();
            setLoadingStage("Narrating outcome…");

            try {
              const agentRunResRaw = execFinalRes?.agent_run_res;
              const agentRunRes =
                typeof agentRunResRaw === "string"
                  ? agentRunResRaw
                  : JSON.stringify(agentRunResRaw);

              const narrateRes = await fetch(
                `${apiBase}/orc/narrate-run-outcome`,
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    agent_run_res: agentRunRes,
                    client_id: clientId,
                    session_id: sessionId,
                    ...(lastEmailRef.current
                      ? { email: lastEmailRef.current }
                      : {}),
                  }),
                }
              );
              const narrateJson = await narrateRes.json().catch(() => ({}));
              if (!narrateRes.ok)
                throw new Error(
                  narrateJson?.error || `HTTP ${narrateRes.status}`
                );

              const text = narrateJson?.text ?? narrateJson?.answer ?? null;
              if (text) {
                onAnswered?.({ answer: text });

                // Generate artifacts (LEGACY)
                // try {
                //   const artifactsRes = await fetch(
                //     `${apiBase}/orc/generate_artifacts`,
                //     {
                //       method: "POST",
                //       headers: { "Content-Type": "application/json" },
                //       body: JSON.stringify({
                //         question: q.trim(),
                //         narration: text,
                //         client_id: clientId,
                //         session_id: sessionId,
                //         ...(lastEmailRef.current
                //           ? { email: lastEmailRef.current }
                //           : {}),
                //       }),
                //     }
                //   );
                //   const artifactsJson = await artifactsRes
                //     .json()
                //     .catch(() => ({}));
                //   if (!artifactsRes.ok)
                //     throw new Error(
                //       artifactsJson?.error || `HTTP ${artifactsRes.status}`
                //     );
                //   if (!artifactsLoggedRef.current) {
                //     appendLog("📦 Artifacts generated");
                //     artifactsLoggedRef.current = true;
                //   }
                //   if (artifactsJson) onAnswered?.({ artifacts: artifactsJson });
                // } catch (err) {
                //   appendLog(
                //     `❌ Artifact generation error: ${
                //       err?.message || String(err)
                //     }`
                //   );
                //   setError(err?.message || String(err));
                // }
              } else {
                const fallback =
                  typeof execFinalRes?.agent_run_res === "string"
                    ? execFinalRes.agent_run_res
                    : JSON.stringify(execFinalRes?.agent_run_res, null, 2);
                onAnswered?.({ answer: fallback });
              }
            } catch (err) {
              appendLog(`❌ Narrate error: ${err?.message || String(err)}`);
              setError(err?.message || String(err));
              const fallback =
                typeof execFinalRes?.agent_run_res === "string"
                  ? execFinalRes.agent_run_res
                  : JSON.stringify(execFinalRes?.agent_run_res, null, 2);
              onAnswered?.({ answer: fallback });
            } finally {
              setBusy(false);
              setQ("");
              setLoadingStage("");
              onProgressClear?.();
              onStatus?.("Done");
            }
          }
        }; // end doPoll

        // prime + interval
        await doPoll(true);
        firstPollResetDoneRef.current = true;
        if (!didNarrateRef.current) {
          pollTimerRef.current = setInterval(() => {
            doPoll(false).catch((err) => {
              setError(err?.message || String(err));
              appendLog(`❌ Polling error: ${err?.message || String(err)}`);
              stopPolling();
              setBusy(false);
              setLoadingStage("");
            });
          }, 3000);
        }
      } catch (err) {
        setError(err?.message || String(err));
        appendLog(`❌ Error: ${err?.message || String(err)}`);
        setBusy(false);
        setLoadingStage("");
      }
    };

    const onKey = (e) => {
      if (e.isComposing) return;
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        askSpendChat();
      }
    };

    // Render logs with Plan -> bulletized list
    const renderLogs = () => {
      const els = [];
      for (let i = 0; i < logs.length; i++) {
        let line = logs[i];
        if (typeof line === "string" && line.startsWith(PLAN_UPDATE_PREFIX)) {
          line = line.slice(PLAN_UPDATE_PREFIX.length);
        }

        if (line === "Plan") {
          let planBlob = "";
          let j = i + 1;
          while (j < logs.length) {
            const nxt = logs[j];
            if (
              !nxt ||
              nxt === "Plan" ||
              nxt.startsWith("Tool ") ||
              nxt.startsWith("📦") ||
              nxt.startsWith("Summaris")
            )
              break;
            planBlob += (planBlob ? " " : "") + nxt;
            j++;
          }
          const items = bulletize(planBlob);
          els.push(
            <div key={`plan-h-${i}`} style={styles.logHeading}>
              Plan
            </div>
          );
          if (items.length) {
            els.push(
              <ol key={`plan-l-${i}`} style={styles.logList}>
                {items.map((t, idx) => (
                  <li key={idx} style={styles.logListItem}>
                    {t}
                  </li>
                ))}
              </ol>
            );
          } else if (planBlob) {
            els.push(
              <div key={`plan-f-${i}`} style={styles.logLine}>
                {planBlob}
              </div>
            );
          }
          i = j - 1;
          continue;
        }

        els.push(
          <div key={i} style={styles.logLine}>
            {line}
          </div>
        );
      }
      return els;
    };


    return (
      <div style={styles.container}>
        <div
          style={{
            ...styles.inputContainer,
            ...(isFocused ? styles.inputContainerFocused : {}),
          }}
        >
          <div style={styles.inputWrapper}>
            <textarea
              ref={textareaRef}
              placeholder="Type your message here"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={onKey}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              style={styles.textarea}
              disabled={busy}
              rows={1}
            />

            <button
              onClick={askSpendChat}
              disabled={busy || !q.trim()}
              style={{
                ...styles.sendBtn,
                ...(busy || !q.trim() ? styles.sendBtnDisabled : {}),
              }}
              aria-label="Send"
            >
              {busy ? <div style={styles.sendSpinner} /> : <ArrowUp />}
            </button>
          </div>
        </div>
      </div>
    );
  }
);
